import React, { useState, useEffect, useMemo } from "react";
import Select from "react-select";
import {
  Card,
  CardBody,
  Col,
  Container,
  Form,
  Input,
  Label,
  Row,
  Button,
  Table,
} from "reactstrap";
// Using custom Bootstrap pagination for consistency
import { getCompanies, saveCompany } from "../../components/Api/GmsCompany";
import { Eye, SquarePen } from "lucide-react";
import { ClipLoader } from "react-spinners";
import Swal from "sweetalert2";
import API_URL from '../../config';
import { 
  getUserPermissions, 
  hasPagePermissionWithAdmin, 
  hasEditPermissionWithAdmin, 
  hasDeletePermissionWithAdmin 
} from "../../components/Permission/PermissionService";

const defaultForm = {
  company_Id: null,
  company_NameEn: "",
  company_NameAr: "",
  company_Active: true,
  company_LogoPath: null,
  company_Logo: null,
};

const emptyFilter = {
  company_Id: null,
  company_NameEn: "",
  company_NameAr: "",
  company_Active: null,
};

// Add Logo Preview Component
const LogoPreview = ({ file, existingLogo }) => {
  if (!file && !existingLogo) return null;

  let imageUrl;
  if (file) {
    imageUrl = URL.createObjectURL(file);
  } else if (existingLogo) {
    imageUrl = existingLogo.startsWith('http') ? existingLogo : `${API_URL}${existingLogo}`;
  }
  
  return (
    <div className="mt-2">
      <img 
        src={imageUrl} 
        alt="Company Logo" 
        style={{ 
          maxWidth: '200px', 
          maxHeight: '200px', 
          objectFit: 'cover',
          borderRadius: '8px'
        }} 
      />
    </div>
  );
};

const getCompanyLogoDisplayName = (form) => {
  if (form.company_Logo) {
    return form.company_Logo.name;
  }
  if (form.company_LogoPath) {
    return form.company_LogoPath.split("/").pop();
  }
  return "No file chosen";
};

export default function CompanyPage() {
  const [companies, setCompanies] = useState([]);
  const [form, setForm] = useState(defaultForm);
  const [filter, setFilter] = useState(emptyFilter);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [viewLoadingRow, setViewLoadingRow] = useState(null);
  const [editLoadingRow, setEditLoadingRow] = useState(null);
  
  // Permission states
  const [permissions, setPermissions] = useState([]);
  const [permissionsLoading, setPermissionsLoading] = useState(true);
  const [canView, setCanView] = useState(false);
  const [canEdit, setCanEdit] = useState(false);
  const [canDelete, setCanDelete] = useState(false);

  const columns = useMemo(
    () => [
      {
        header: "#",
        accessorKey: "serialNumber",
        enableColumnFilter: false,
        cell: ({ row }) => row.index + 1,
      },
      {
        header: "Name (En)",
        accessorKey: "company_NameEn",
        enableColumnFilter: false,
      },
      {
        header: "Name (Ar)",
        accessorKey: "company_NameAr",
        enableColumnFilter: false,
      },
      {
        header: "Active",
        accessorKey: "company_Active",
        enableColumnFilter: false,
        cell: ({ value }) => (value ? "Yes" : "No"),
      },
      {
        header: "View",
        enableSorting: false,
        enableColumnFilter: false,
        cell: ({ row }) => (
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleView(row.original);
            }}
            className="btn btn-link p-0"
            disabled={viewLoadingRow === row.original.company_Id}
          >
            {viewLoadingRow === row.original.company_Id ? (
              <ClipLoader size={20} color="black" />
            ) : (
              <Eye size={20} color="black" />
            )}
          </button>
        ),
      },
      {
        header: "Edit",
        enableSorting: false,
        enableColumnFilter: false,
        cell: ({ row }) => (
          canEdit ? (
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleEdit(row.original);
              }}
              className="btn btn-link p-0"
              disabled={editLoadingRow === row.original.company_Id}
            >
              {editLoadingRow === row.original.company_Id ? (
                <ClipLoader size={20} color="black" />
              ) : (
                <SquarePen size={20} color="black" />
              )}
            </button>
          ) : null
        ),
      },
    ],
    [viewLoadingRow, editLoadingRow, canEdit]
  );

  // Load permissions first
  useEffect(() => {
    loadPermissions();
  }, []);

  // Load companies after permissions are loaded
  useEffect(() => {
    if (!permissionsLoading && canView) {
      loadCompanies();
    }
  }, [permissionsLoading, canView]);

  const loadPermissions = async () => {
    try {
      const userPermissions = await getUserPermissions();
      setPermissions(userPermissions);
      
      // Check permissions for this page
      const hasView = hasPagePermissionWithAdmin(userPermissions, '/GmsCompany');
      const hasEdit = hasEditPermissionWithAdmin(userPermissions, '/GmsCompany');
      const hasDelete = hasDeletePermissionWithAdmin(userPermissions, '/GmsCompany');
      
      setCanView(hasView);
      setCanEdit(hasEdit);
      setCanDelete(hasDelete);
      
      // If no view permission, redirect to dashboard
      if (!hasView) {
        Swal.fire("Access Denied", "You don't have permission to view this page.", "error").then(() => {
          window.location.href = '/Dashboard';
        });
      }
    } catch (error) {
      console.error("Error loading permissions:", error);
      Swal.fire("Error", "Failed to load permissions. Redirecting to dashboard.", "error").then(() => {
        window.location.href = '/Dashboard';
      });
    } finally {
      setPermissionsLoading(false);
    }
  };

  const loadCompanies = async (filterData = emptyFilter) => {
    setLoading(true);
    try {
      const data = await getCompanies(filterData);
      setCompanies(data);
      setTotalCount(data.length);
    } catch (error) {
      Swal.fire("Error", "Failed to load companies: " + error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleFormChange = (field, value) => {
    setForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Set default active status based on permissions
  useEffect(() => {
    if (!isEditing && canEdit && !canDelete) {
      setForm(prev => ({
        ...prev,
        company_Active: true
      }));
    }
  }, [isEditing, canEdit, canDelete]);

  const handleFilterChange = (field, value) => {
    setFilter(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const formData = new FormData();
      
      // Handle logo file
      if (form.company_Logo) {
        formData.append('logoFile', form.company_Logo);
        console.log('New logo file being sent:', {
          name: form.company_Logo.name,
          type: form.company_Logo.type,
          size: form.company_Logo.size
        });
      }
      
      // Add all form fields to FormData
      Object.keys(form).forEach(key => {
        if (form[key] !== null && form[key] !== undefined && key !== 'company_Logo') {
          formData.append(key, form[key]);
        }
      });
      
      const result = await saveCompany(formData);
      Swal.fire("Success", result.message, "success");
      setForm(defaultForm);
      setIsEditing(false);
      loadCompanies(filter);
    } catch (error) {
      Swal.fire("Error", "Failed to save group: " + error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleView = async (company) => {
    setViewLoadingRow(company.company_Id);
    try {
      Swal.fire({
        title: company.company_NameEn,
        html: `
          <div class="text-left">
            <div class="mb-3 text-center">
              ${company.company_LogoPath ? 
                `<img src="${API_URL}${company.company_LogoPath}" alt="${company.company_NameEn}" style="width: 100px; height: 100px; border-radius: 50%; object-fit: cover; margin-bottom: 10px;">` : 
                '<div style="width: 100px; height: 100px; border-radius: 50%; background-color: #e9ecef; display: flex; align-items: center; justify-content: center; margin: 0 auto 10px;"><i class="bx bx-building" style="font-size: 40px; color: #adb5bd;"></i></div>'
              }
            </div>
            <div class="mb-3">
              <h6 class="mb-2">Basic Information</h6>
              <p><strong>Name (English):</strong> ${company.company_NameEn}</p>
              <p><strong>Name (Arabic):</strong> ${company.company_NameAr}</p>
              <p><strong>Status:</strong> ${company.company_Active ? "Active" : "Inactive"}</p>
            </div>
          </div>
        `,
        icon: "info",
        width: '600px',
        customClass: {
          container: 'custom-swal-container',
          popup: 'custom-swal-popup',
          content: 'custom-swal-content'
        }
      });
    } catch (error) {
              Swal.fire("Error", "Failed to view group details", "error");
    } finally {
      setViewLoadingRow(null);
    }
  };

  const handleEdit = (company) => {
    setForm(company);
    setIsEditing(true);
    window.scrollTo(0, 0);
  };

  const handleCancelEdit = () => {
    setForm(defaultForm);
    setIsEditing(false);
  };

  const handleApplyFilter = () => {
    setPage(1);
    loadCompanies(filter);
  };

  const handleClearFilter = () => {
    setFilter(emptyFilter);
    setPage(1);
    loadCompanies(emptyFilter);
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  // Get paginated data for display
  const getPaginatedCompanies = () => {
    const startIndex = (page - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return companies.slice(startIndex, endIndex);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      return new Date(dateString).toLocaleDateString();
    } catch (error) {
      return '-';
    }
  };

  // Options for searchable dropdowns
  const companyOptions = useMemo(() => 
    companies.map(company => ({
      value: company.company_Id,
      label: `${company.company_Id} - ${company.company_NameEn}`
    }))
  , [companies]);

  const nameEnOptions = useMemo(() => 
    companies.map(company => ({
      value: company.company_NameEn,
      label: company.company_NameEn
    }))
  , [companies]);

  const nameArOptions = useMemo(() => 
    companies.map(company => ({
      value: company.company_NameAr,
      label: company.company_NameAr
    }))
  , [companies]);

  const statusOptions = [
    { value: true, label: "Active" },
    { value: false, label: "Inactive" },
  ];

  // Show loading spinner while permissions are loading
  if (permissionsLoading) {
    return (
      <div className="page-content">
        <Container fluid>
          <Row>
            <Col xs={12}>
              <Card>
                <CardBody className="text-center py-5">
                  <ClipLoader size={50} color="#556ee6" />
                  <p className="mt-3">Loading permissions...</p>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    );
  }

  // Don't render if no view permission
  if (!canView) {
    return null;
  }

  return (
    <div className="page-content">
      <Container fluid>
        <Row>
          <Col xs={12}>
            <Card>
              <CardBody>
                <h4 className="card-title mb-4">Group Management</h4>

                {/* Form Section - Only show if user has edit permission */}
                {canEdit && (
                  <Card className="mb-4">
                    <CardBody>
                      <h5 className="card-title mb-3">
                        {isEditing ? "Edit Group" : "Add New Group"}
                      </h5>
                    <Form onSubmit={handleSubmit}>
                      <Row>
                        <Col md={6}>
                          <div className="mb-3">
                            <Label>Name (EN)</Label>
                            <Input
                              name="company_NameEn"
                              value={form.company_NameEn}
                              onChange={(e) => handleFormChange("company_NameEn", e.target.value)}
                              required
                            />
                          </div>
                        </Col>
                        <Col md={6}>
                          <div className="mb-3">
                            <Label>Name (AR)</Label>
                            <Input
                              name="company_NameAr"
                              value={form.company_NameAr}
                              onChange={(e) => handleFormChange("company_NameAr", e.target.value)}
                              required
                            />
                          </div>
                        </Col>
                      </Row>

                      <Row className="mb-3">
                        <Col md={6}>
                          <div className="mb-3">
                            <Label>Group Logo</Label>
                            <input
                              key={form.company_Logo ? form.company_Logo.name : 'file-input'}
                              id="companyLogoUpload"
                              type="file"
                              className="d-none"
                              accept="image/*"
                              onChange={(e) => handleFormChange("company_Logo", e.target.files[0])}
                            />
                            <div className="input-group">
                              <label className="input-group-text mb-0" htmlFor="companyLogoUpload">
                                Choose File
                              </label>
                              <input
                                type="text"
                                className="form-control"
                                value={getCompanyLogoDisplayName(form)}
                                readOnly
                              />
                            </div>
                            <LogoPreview 
                              file={form.company_Logo} 
                              existingLogo={form.company_LogoPath} 
                            />
                          </div>
                        </Col>
                      </Row>

                      <Row className="mb-3">
                        <Col md={6}>
                          <div className="form-check form-switch">
                            <input
                              type="checkbox"
                              className="form-check-input"
                              id="company_Active"
                              name="company_Active"
                              checked={form.company_Active}
                              onChange={(e) => handleFormChange("company_Active", e.target.checked)}
                              disabled={!canDelete} // Only allow status change if user has delete permission
                            />
                            <label className="form-check-label" htmlFor="company_Active">
                              {form.company_Active ? "Active" : "Inactive"}
                            </label>
                          </div>
                        </Col>
                      </Row>

                      <div className="d-flex gap-2">
                        <Button
                          type="submit"
                          color="success"
                          disabled={loading}
                        >
                          {loading ? (
                            <>
                              <ClipLoader size={20} color="white" />{" "}
                              <span className="ms-2">Saving...</span>
                            </>
                          ) : isEditing ? (
                            "Update Group"
                          ) : (
                            "Save Group"
                          )}
                        </Button>
                        {isEditing && (
                          <Button
                            type="button"
                            color="secondary"
                            onClick={handleCancelEdit}
                          >
                            Cancel
                          </Button>
                        )}
                      </div>
                    </Form>
                  </CardBody>
                </Card>
                )}

                {/* Filter Section */}
                <Card className="mb-4">
                  <CardBody>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h5 className="card-title mb-0">Filter Groups</h5>
                      <Button
                        color="primary"
                        onClick={() => setShowFilters(!showFilters)}
                      >
                        {showFilters ? "Hide Filters" : "Show Filters"}
                      </Button>
                    </div>

                    {showFilters && (
                      <>
                        <Row className="mb-3">
                          <Col md={4}>
                            <div className="mb-3">
                              <Label>Name (EN)</Label>
                              <Select
                                isClearable
                                value={
                                  filter.company_NameEn
                                    ? nameEnOptions.find(
                                        option => option.value === filter.company_NameEn
                                      )
                                    : null
                                }
                                onChange={(option) =>
                                  handleFilterChange(
                                    "company_NameEn",
                                    option ? option.value : ""
                                  )
                                }
                                options={nameEnOptions}
                              />
                            </div>
                          </Col>
                          <Col md={4}>
                            <div className="mb-3">
                              <Label>Name (AR)</Label>
                              <Select
                                isClearable
                                value={
                                  filter.company_NameAr
                                    ? nameArOptions.find(
                                        option => option.value === filter.company_NameAr
                                      )
                                    : null
                                }
                                onChange={(option) =>
                                  handleFilterChange(
                                    "company_NameAr",
                                    option ? option.value : ""
                                  )
                                }
                                options={nameArOptions}
                              />
                            </div>
                          </Col>
                          <Col md={4}>
                            <div className="mb-3">
                              <Label>Status</Label>
                              <Select
                                isClearable
                                value={
                                  filter.company_Active !== null
                                    ? statusOptions.find(
                                        option => option.value === filter.company_Active
                                      )
                                    : null
                                }
                                onChange={(option) =>
                                  handleFilterChange(
                                    "company_Active",
                                    option ? option.value : null
                                  )
                                }
                                options={statusOptions}
                              />
                            </div>
                          </Col>
                        </Row>
                        <div className="d-flex gap-2">
                          <Button color="primary" onClick={handleApplyFilter}>
                            Apply
                          </Button>
                          <Button color="warning" onClick={handleClearFilter}>
                            Clear
                          </Button>
                        </div>
                      </>
                    )}
                  </CardBody>
                </Card>

                {/* Table Section */}
                <Card>
                  <CardBody>
                    <h5 className="card-title mb-3">
                      Group List ({totalCount} records)
                    </h5>
                    <div className="table-responsive">
                      <Table bordered>
                        <thead className="table-light">
                          <tr>
                            <th>Sr.No</th>
                            <th>Logo</th>
                            <th>Name (EN)</th>
                            <th>Name (AR)</th>
                                                  <th>Active</th>
                      <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {companies.length === 0 ? (
                            <tr>
                              <td colSpan="6" className="text-center">
                                No groups found
                              </td>
                            </tr>
                          ) : (
                            getPaginatedCompanies().map((company, index) => (
                              <tr key={company.company_Id}>
                                <td>{(page - 1) * pageSize + index + 1}</td>
                                <td>
                                  {company.company_LogoPath ? (
                                    <img
                                      src={`${API_URL}${company.company_LogoPath}`}
                                      alt={company.company_NameEn}
                                      style={{
                                        width: '40px',
                                        height: '40px',
                                        borderRadius: '50%',
                                        objectFit: 'cover'
                                      }}
                                    />
                                  ) : (
                                    <div
                                      style={{
                                        width: '40px',
                                        height: '40px',
                                        borderRadius: '50%',
                                        backgroundColor: '#e9ecef',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                      }}
                                    >
                                      <i className="bx bx-building" />
                                    </div>
                                  )}
                                </td>
                                <td>{company.company_NameEn}</td>
                                <td>{company.company_NameAr}</td>
                                <td>
                                  <span className={`badge ${company.company_Active ? 'bg-success' : 'bg-danger'}`}>
                                    {company.company_Active ? 'Active' : 'Inactive'}
                                  </span>
                                </td>

                                <td>
                                  <div className="d-flex gap-2">
                                    {canEdit && (
                                      <Button
                                        color="info"
                                        size="sm"
                                        onClick={() => handleEdit(company)}
                                      >
                                        Edit
                                      </Button>
                                    )}
                                    <Button
                                      color="primary"
                                      size="sm"
                                      onClick={() => handleView(company)}
                                    >
                                      View
                                    </Button>
                                  </div>
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </Table>
                    </div>

                    <div className="d-flex justify-content-between align-items-center mt-4 p-3" 
                         style={{backgroundColor: '#f8f9fa', borderRadius: '0.375rem', border: '1px solid #dee2e6'}}>
                      <div className="text-muted" style={{fontSize: '14px', fontWeight: '500'}}>
                        <i className="mdi mdi-information-outline me-2" style={{color: '#556ee6'}}></i>
                        Showing <span style={{color: '#556ee6', fontWeight: '600'}}>{totalCount > 0 ? (page - 1) * pageSize + 1 : 0}</span> to <span style={{color: '#556ee6', fontWeight: '600'}}>{Math.min(page * pageSize, totalCount)}</span> of <span style={{color: '#556ee6', fontWeight: '600'}}>{totalCount}</span> entries
                      </div>
                      {totalCount > pageSize && (
                        <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                          <Button
                            color="light"
                            size="sm"
                            disabled={page === 1}
                            onClick={() => handlePageChange(page - 1)}
                            style={{
                              border: '1px solid #dee2e6',
                              borderRadius: '0.375rem',
                              minWidth: '32px',
                              height: '32px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backgroundColor: page === 1 ? '#f8f9fa' : '#fff',
                              color: page === 1 ? '#6c757d' : '#495057'
                            }}
                          >
                            <i className="mdi mdi-chevron-left"></i>
                          </Button>
                          
                          {[...Array(Math.ceil(totalCount / pageSize))].map((_, index) => {
                            const pageNum = index + 1;
                            const isActive = page === pageNum;
                            
                            return (
                              <Button
                                key={pageNum}
                                color={isActive ? "primary" : "light"}
                                size="sm"
                                onClick={() => handlePageChange(pageNum)}
                                style={{
                                  border: '1px solid #dee2e6',
                                  borderRadius: '0.375rem',
                                  minWidth: '32px',
                                  height: '32px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  backgroundColor: isActive ? '#556ee6' : '#fff',
                                  borderColor: isActive ? '#556ee6' : '#dee2e6',
                                  color: isActive ? '#fff' : '#495057',
                                  fontWeight: isActive ? '600' : '400',
                                  boxShadow: isActive ? '0 2px 4px rgba(85,110,230,0.25)' : 'none'
                                }}
                              >
                                {pageNum}
                              </Button>
                            );
                          })}
                          
                          <Button
                            color="light"
                            size="sm"
                            disabled={page === Math.ceil(totalCount / pageSize)}
                            onClick={() => handlePageChange(page + 1)}
                            style={{
                              border: '1px solid #dee2e6',
                              borderRadius: '0.375rem',
                              minWidth: '32px',
                              height: '32px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backgroundColor: page === Math.ceil(totalCount / pageSize) ? '#f8f9fa' : '#fff',
                              color: page === Math.ceil(totalCount / pageSize) ? '#6c757d' : '#495057'
                            }}
                          >
                            <i className="mdi mdi-chevron-right"></i>
                          </Button>
                        </div>
                      )}
                    </div>
                  </CardBody>
                </Card>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
}
